import _ from 'lodash';
import moment from 'moment';
import React from 'react';

import { ColorIcon } from 'common/styles/oc-styled-components';
import { colors } from 'common/styles/style-constants';

import { Info } from '../components/ServicesNeedToKnow/styled-components';
import { DENTAL_REASON_ID, DENTAL_SERVICES } from '../constants/constants';
import { ProfileReasonName } from '../profile-styled-components';

const clinicOffersService = (allClinicServiceIds, serviceId) =>
  !!allClinicServiceIds.find((id) => id === serviceId)
  || serviceId === DENTAL_REASON_ID.checkup
  || serviceId === DENTAL_REASON_ID.urgent;

const userWantsService = (serviceId, userReason) =>
  serviceId === _.get(userReason, 'overall.id')
  || _.includes(userReason, serviceId);

// Helper function to determine if a service is picked
const isServicePicked = (serviceId, overallReason, userSubReasons) => {
  const pickedForOverallReason = userWantsService(serviceId, overallReason);
  const pickedForUserSubReasons =
    userSubReasons && userWantsService(serviceId, userSubReasons);
  return pickedForOverallReason || pickedForUserSubReasons;
};

export const markServicesWithOfferedAndPicked = (
  allClinicServiceIds,
  services,
  overallReason,
  userSubReasons,
) => {
  if (!services) {
    return undefined;
  }
  return services.map((service) => ({
    ...service,
    offered: clinicOffersService(allClinicServiceIds, service.id),
    picked: isServicePicked(service.id, overallReason, userSubReasons),
  }));
};

export const filterPickedServices = (services) =>
  services.filter((service) => service.picked);

export const getAllClinicServiceIds = (providers) => {
  if (!providers) {
    return [];
  }
  // use a set so every value is only stored once.
  const clinicServices = new Set();
  providers.forEach((provider) => {
    const { offeredServices } = provider;
    return offeredServices.map((offeredService) =>
      clinicServices.add(offeredService.service.id));
  });

  return Array.from(clinicServices);
};

export const filterAndSortServices = (allServices, isDentistry) => {
  if (!allServices) {
    return [];
  }

  let servicesFiltered = allServices;

  if (isDentistry) {
    servicesFiltered = servicesFiltered.filter((service) =>
      _.has(DENTAL_SERVICES, service.id));
    servicesFiltered.push({
      name: 'Checkup & Cleaning',
      id: DENTAL_REASON_ID.checkup,
    });
    servicesFiltered.push({
      name: 'Urgent Issue',
      id: DENTAL_REASON_ID.urgent,
    });
  }

  return _.sortBy(_.uniq(servicesFiltered), 'name');
};

const organizeSlotsByAvailabilityPreference = (slots) => {
  const openWeekends = slots.some(
    (slot) => moment(slot.date).day() === 0 || moment(slot.date).day() === 6,
  );
  const openEvenings = slots.some(
    (slot) => slot.time < 1440 && slot.time >= 1020,
  );
  const openEarly = slots.some((slot) => slot.time <= 540);

  return { openWeekends, openEvenings, openEarly };
};

export const getInsights = (
  clinic,
  availabilities,
  anxiety,
  performanceBadges,
  superPracticeBadges = {},
) => {
  const insights = [];
  const { bookedByPastMonth, providers, slots, features } = clinic;
  const { speedyResponse = {}, scheduleMaster = {}, patientsChoice = {} } =
    superPracticeBadges || {};
  if (bookedByPastMonth > 0) {
    insights.push({
      kind: 'newPatients',
      title: 'Accepting New Patients',
      text: `${bookedByPastMonth} new ${
        bookedByPastMonth !== 1 ? 'patients' : 'patient'
      } last 30 days`,
      color: colors.coral,
    });
  }

  // the hasExperiencedProvider info lives on the provider endpoint, not on the provider object that is returned from the clinic endpoint
  // todo: is it worth calling the endpoint just for this badge? this was legacy from when the results were provider centric - I think we should refactor the clinic endpoint to include this info instead. added a tech debt ticket.
  const hasExperiencedProvider = providers.some(
    (provider) => provider.doctor.yearsExperience >= 10,
  );

  if (hasExperiencedProvider) {
    insights.push({
      kind: 'highlyExperienced',
      title: 'Highly experienced',
      text: 'Practicing for 10+ years',
      color: colors.coral,
    });
  }

  if (speedyResponse.achieved) {
    insights.push({
      kind: 'firstResponseTimeHours',
      title: 'Speedy Responder',
      text: 'Responds within 2 hrs',
      color: colors.yellow,
    });
  }

  if (performanceBadges.includes('gold-responseRate')) {
    insights.push({
      kind: 'responseRate',
      title: 'Reliable Responder',
      text: 'Goes above and beyond for new patients',
      color: colors.blue,
    });
  }

  if (scheduleMaster.achieved) {
    insights.push({
      kind: 'scheduleRate',
      title: 'Schedule Master',
      text: 'Adapts to your availability preferences',
      color: colors.blue,
    });
  }

  if (patientsChoice.achieved) {
    insights.push({
      kind: 'patientsChoice',
      title: "Patient's Choice",
      text: 'Average rating over 4.8 stars',
      color: colors.sage,
    });
  }

  const slotsByAvailability = organizeSlotsByAvailabilityPreference(
    slots || [],
  );

  if (availabilities) {
    availabilities.forEach((pref) => {
      if (pref === 'weekend' && slotsByAvailability.openWeekends) {
        insights.push({
          kind: 'weekendAvailability',
          title: 'Available Weekends',
          text: 'Open on the weekend',
          color: colors.coral,
        });
      } else if (pref === 'evening' && slotsByAvailability.openEvenings) {
        insights.push({
          kind: 'eveningAvailability',
          title: 'Available Evenings',
          text: 'Open for evening appointments',
          color: colors.coral,
        });
      } else if (pref === 'early' && slotsByAvailability.openEarly) {
        insights.push({
          kind: 'earlyAvailability',
          title: 'Available Early',
          text: 'Open for early morning appointments',
        });
      }
    });
  }

  if (
    anxiety
    && anxiety !== 'Not at all nervous'
    && features
    && features.some((feature) => feature.id === 16)
  ) {
    insights.push({
      kind: 'dentalAnxietyFriendly',
      title: 'Dental anxiety friendly',
      text: 'Offers a special experience for anxious patients',
      color: colors.coral,
    });
  }
  return insights;
};

export const getInsuranceName = (insurance) => {
  const providerName = _.get(insurance, 'provider');
  let planName = _.get(insurance, 'plan') || _.get(insurance, 'type');
  if (planName === 'Unsure') {
    planName = null;
  }
  if (!planName) {
    return providerName;
  }
  return `${providerName} - ${planName}`;
};

// Renders list of all services offered, leaving out those that aren't and adding a checkmark beside the ones that were selected
export const listAllServices = (reasonsMarked) =>
  reasonsMarked.map(
    (reason) =>
      (reason.offered || reason.picked)
      && (
        <Info key={reason.id} as="li">
          <ProfileReasonName>
            {reason.name}
            {reason.picked
            && (
              <ColorIcon
                color={reason.offered ? colors.sage : colors.danger}
                margin="0 0 0 5px"
                theme="filled"
                type={reason.offered ? 'check-circle' : 'close-circle'}
              />
            )}
          </ProfileReasonName>
        </Info>
      )

    ,
  );
